/**
 * Unsubscribe Page - STORY-051
 *
 * Public page for managing email preferences:
 * - Full unsubscription option
 * - Reduce frequency option
 * - Feedback collection
 * - Resubscription option
 */

import { useState, useEffect } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import { Card } from '@/components/ui/Card'
import { Button } from '@/components/ui/Button'
import { Spinner } from '@/components/ui/Spinner'
import {
  useSubscriberByToken,
  useUpdateSubscriberPreferences,
  useResubscribe,
} from '@/hooks/useMarketing'
import type { EmailFrequency, UnsubscribeReason, UnsubscribeFormData } from '@/types/marketing'
import {
  EMAIL_FREQUENCY_LABELS,
  UNSUBSCRIBE_REASON_LABELS,
} from '@/types/marketing'
import {
  Mail,
  MailX,
  Check,
  AlertCircle,
  Clock,
  RefreshCw,
  ChevronDown,
  ChevronUp,
} from 'lucide-react'

function NotFound() {
  return (
    <div className="min-h-screen bg-beige-50 flex items-center justify-center p-4">
      <Card className="max-w-md text-center p-8">
        <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-500" />
        <h1 className="text-xl font-bold text-brown-900 mb-2">Lien invalide</h1>
        <p className="text-brown-600">
          Ce lien de desinscription n'est pas valide ou a expire.
          Veuillez utiliser le lien present dans un de nos emails recents.
        </p>
      </Card>
    </div>
  )
}

function LoadingState() {
  return (
    <div className="min-h-screen bg-beige-50 flex items-center justify-center">
      <Spinner size="lg" />
    </div>
  )
}

export default function UnsubscribePage() {
  const { token } = useParams<{ token: string }>()
  const [searchParams] = useSearchParams()
  const showPreferences = searchParams.get('preferences') === 'true'

  const [step, setStep] = useState<'options' | 'confirm' | 'done'>('options')
  const [selectedOption, setSelectedOption] = useState<'frequency' | 'unsubscribe' | null>(null)
  const [selectedFrequency, setSelectedFrequency] = useState<EmailFrequency>('weekly')
  const [selectedReason, setSelectedReason] = useState<UnsubscribeReason>('user_request')
  const [feedback, setFeedback] = useState('')
  const [showReasons, setShowReasons] = useState(false)
  const [actionSuccess, setActionSuccess] = useState<'unsubscribed' | 'updated' | 'resubscribed' | null>(null)

  // Queries
  const { data: subscriber, isLoading, error } = useSubscriberByToken(token || '')

  // Mutations
  const updatePreferencesMutation = useUpdateSubscriberPreferences()
  const resubscribeMutation = useResubscribe()

  // Set default step based on URL params
  useEffect(() => {
    if (showPreferences) {
      setSelectedOption('frequency')
    }
  }, [showPreferences])

  // Check if already unsubscribed
  const isUnsubscribed = subscriber?.status === 'unsubscribed'

  const handleUpdateFrequency = async () => {
    if (!subscriber) return

    try {
      await updatePreferencesMutation.mutateAsync({
        email: subscriber.email,
        data: {
          email_frequency: selectedFrequency,
          full_unsubscribe: false,
        },
      })
      setActionSuccess('updated')
      setStep('done')
    } catch (error) {
      console.error('Failed to update preferences:', error)
    }
  }

  const handleUnsubscribe = async () => {
    if (!subscriber) return

    try {
      await updatePreferencesMutation.mutateAsync({
        email: subscriber.email,
        data: {
          full_unsubscribe: true,
          unsubscribe_reason: selectedReason,
          unsubscribe_feedback: feedback || undefined,
        },
      })
      setActionSuccess('unsubscribed')
      setStep('done')
    } catch (error) {
      console.error('Failed to unsubscribe:', error)
    }
  }

  const handleResubscribe = async () => {
    if (!subscriber) return

    try {
      await resubscribeMutation.mutateAsync(subscriber.email)
      setActionSuccess('resubscribed')
      setStep('done')
    } catch (error) {
      console.error('Failed to resubscribe:', error)
    }
  }

  if (isLoading) return <LoadingState />
  if (error || !subscriber) return <NotFound />

  return (
    <div className="min-h-screen bg-beige-50 py-12 px-4">
      <div className="max-w-lg mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <h1 className="text-2xl font-display font-bold text-brown-900">LesSablesWeb</h1>
          <p className="text-brown-500 text-sm">Gestion des preferences email</p>
        </div>

        <Card className="p-8">
          {/* Success state */}
          {step === 'done' && (
            <div className="text-center">
              {actionSuccess === 'unsubscribed' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-red-100 flex items-center justify-center">
                    <MailX className="w-8 h-8 text-red-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-brown-900 mb-2">
                    Desinscription confirmee
                  </h2>
                  <p className="text-brown-600 mb-6">
                    Vous ne recevrez plus nos emails marketing.
                    Nous regrettons de vous voir partir.
                  </p>
                  <p className="text-sm text-brown-500 mb-4">
                    Vous avez change d'avis ?
                  </p>
                  <Button
                    variant="outline"
                    onClick={handleResubscribe}
                    isLoading={resubscribeMutation.isPending}
                    leftIcon={<RefreshCw className="w-4 h-4" />}
                  >
                    Se reinscrire
                  </Button>
                </>
              )}

              {actionSuccess === 'updated' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-mint-100 flex items-center justify-center">
                    <Check className="w-8 h-8 text-mint-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-brown-900 mb-2">
                    Preferences mises a jour
                  </h2>
                  <p className="text-brown-600">
                    Vos preferences email ont ete mises a jour avec succes.
                    Vous recevrez desormais: <strong>{EMAIL_FREQUENCY_LABELS[selectedFrequency]}</strong>
                  </p>
                </>
              )}

              {actionSuccess === 'resubscribed' && (
                <>
                  <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-mint-100 flex items-center justify-center">
                    <Mail className="w-8 h-8 text-mint-600" />
                  </div>
                  <h2 className="text-xl font-semibold text-brown-900 mb-2">
                    Reinscription confirmee
                  </h2>
                  <p className="text-brown-600">
                    Vous etes a nouveau inscrit a notre newsletter.
                    Nous sommes heureux de vous retrouver !
                  </p>
                </>
              )}
            </div>
          )}

          {/* Already unsubscribed state */}
          {step === 'options' && isUnsubscribed && (
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-gray-100 flex items-center justify-center">
                <MailX className="w-8 h-8 text-gray-600" />
              </div>
              <h2 className="text-xl font-semibold text-brown-900 mb-2">
                Vous etes deja desinscrit
              </h2>
              <p className="text-brown-600 mb-6">
                Vous vous etes desinscrit le {new Date(subscriber.unsubscribed_at || '').toLocaleDateString('fr-FR')}.
              </p>
              <p className="text-sm text-brown-500 mb-4">
                Vous souhaitez vous reinscrire ?
              </p>
              <Button
                onClick={handleResubscribe}
                isLoading={resubscribeMutation.isPending}
                leftIcon={<Mail className="w-4 h-4" />}
              >
                Se reinscrire
              </Button>
            </div>
          )}

          {/* Options state */}
          {step === 'options' && !isUnsubscribed && (
            <>
              <div className="text-center mb-8">
                <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-beige-100 flex items-center justify-center">
                  <Mail className="w-8 h-8 text-brown-600" />
                </div>
                <h2 className="text-xl font-semibold text-brown-900 mb-2">
                  Gerer vos emails
                </h2>
                <p className="text-brown-600">
                  Bonjour <strong>{subscriber.full_name || subscriber.email}</strong>,<br />
                  que souhaitez-vous faire ?
                </p>
              </div>

              <div className="space-y-4">
                {/* Reduce frequency option */}
                <button
                  onClick={() => setSelectedOption('frequency')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                    selectedOption === 'frequency'
                      ? 'border-mint-500 bg-mint-50'
                      : 'border-beige-200 hover:border-beige-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      selectedOption === 'frequency' ? 'bg-mint-100' : 'bg-beige-100'
                    }`}>
                      <Clock className={`w-5 h-5 ${
                        selectedOption === 'frequency' ? 'text-mint-600' : 'text-brown-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-brown-900">Reduire la frequence</h3>
                      <p className="text-sm text-brown-600">
                        Recevez moins d'emails tout en restant informe
                      </p>
                    </div>
                  </div>
                </button>

                {/* Frequency selector */}
                {selectedOption === 'frequency' && (
                  <div className="ml-13 pl-4 border-l-2 border-mint-200 space-y-2">
                    {(['weekly', 'monthly', 'important_only'] as EmailFrequency[]).map((freq) => (
                      <label
                        key={freq}
                        className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                          selectedFrequency === freq
                            ? 'bg-mint-100'
                            : 'bg-beige-50 hover:bg-beige-100'
                        }`}
                      >
                        <input
                          type="radio"
                          name="frequency"
                          value={freq}
                          checked={selectedFrequency === freq}
                          onChange={(e) => setSelectedFrequency(e.target.value as EmailFrequency)}
                          className="w-4 h-4 text-mint-600"
                        />
                        <span className="text-brown-700">{EMAIL_FREQUENCY_LABELS[freq]}</span>
                      </label>
                    ))}
                    <Button
                      className="w-full mt-4"
                      onClick={handleUpdateFrequency}
                      isLoading={updatePreferencesMutation.isPending}
                    >
                      Enregistrer mes preferences
                    </Button>
                  </div>
                )}

                {/* Unsubscribe option */}
                <button
                  onClick={() => setSelectedOption('unsubscribe')}
                  className={`w-full p-4 rounded-lg border-2 text-left transition-colors ${
                    selectedOption === 'unsubscribe'
                      ? 'border-red-400 bg-red-50'
                      : 'border-beige-200 hover:border-beige-300'
                  }`}
                >
                  <div className="flex items-start gap-3">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      selectedOption === 'unsubscribe' ? 'bg-red-100' : 'bg-beige-100'
                    }`}>
                      <MailX className={`w-5 h-5 ${
                        selectedOption === 'unsubscribe' ? 'text-red-600' : 'text-brown-600'
                      }`} />
                    </div>
                    <div>
                      <h3 className="font-semibold text-brown-900">Se desinscrire completement</h3>
                      <p className="text-sm text-brown-600">
                        Ne plus recevoir aucun email marketing
                      </p>
                    </div>
                  </div>
                </button>

                {/* Unsubscribe form */}
                {selectedOption === 'unsubscribe' && (
                  <div className="ml-13 pl-4 border-l-2 border-red-200 space-y-4">
                    <p className="text-sm text-brown-600">
                      Nous sommes desoles de vous voir partir.
                      Pouvez-vous nous dire pourquoi ?
                    </p>

                    <div className="space-y-2">
                      {(Object.entries(UNSUBSCRIBE_REASON_LABELS) as [UnsubscribeReason, string][]).map(
                        ([reason, label]) => (
                          <label
                            key={reason}
                            className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition-colors ${
                              selectedReason === reason
                                ? 'bg-red-100'
                                : 'bg-beige-50 hover:bg-beige-100'
                            }`}
                          >
                            <input
                              type="radio"
                              name="reason"
                              value={reason}
                              checked={selectedReason === reason}
                              onChange={(e) => setSelectedReason(e.target.value as UnsubscribeReason)}
                              className="w-4 h-4 text-red-600"
                            />
                            <span className="text-brown-700">{label}</span>
                          </label>
                        )
                      )}
                    </div>

                    {/* Feedback textarea */}
                    <div>
                      <button
                        onClick={() => setShowReasons(!showReasons)}
                        className="flex items-center gap-2 text-sm text-brown-500 hover:text-brown-700"
                      >
                        {showReasons ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        Ajouter un commentaire (optionnel)
                      </button>
                      {showReasons && (
                        <textarea
                          value={feedback}
                          onChange={(e) => setFeedback(e.target.value)}
                          placeholder="Dites-nous comment nous pouvons nous ameliorer..."
                          className="mt-2 w-full px-4 py-2 border border-beige-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent resize-none"
                          rows={3}
                        />
                      )}
                    </div>

                    <Button
                      variant="danger"
                      className="w-full"
                      onClick={handleUnsubscribe}
                      isLoading={updatePreferencesMutation.isPending}
                    >
                      Confirmer la desinscription
                    </Button>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>

        {/* Footer */}
        <div className="mt-8 text-center text-xs text-brown-400">
          <p>LesSablesWeb - SIRET: XXX XXX XXX XXXXX</p>
          <p className="mt-1">Les Sables-d'Olonne, France</p>
        </div>
      </div>
    </div>
  )
}
